import React, {useCallback, useEffect, useMemo, useState} from "react";
import Utils from "../../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {ReactComponent as LostSearchSubmitted} from "../../../../../../../../assets/images/art-like/lost-search-submitted.svg";
import Api from "../../../../../../../../core/services/api/api";
import {Box, Card, Chip, Fade, TextField, Typography} from "@mui/material";
import {AddOutlined, CloseOutlined} from "@mui/icons-material";
import classnames from "classnames";
import Button from "../../../../../../../components/base/button/button";
import {NexusSearchSchemaEntryNames, ProductTechnicalPropertyTypeIds} from "../../../../../../../../core/constants/enums";

const initialState = {imageFile: null, imageDataUrl: null, message: ''};

const LostSearchContainer = ({searchToken, searchSchema, searchData, setSearchData, showForm, readonly}) => {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [removedFilters, setRemovedFilters] = useState([]);
    const [state, setState] = useState(initialState);
    const isMounted = useIsMounted();

    const varietyFilterId = useMemo(() => searchSchema.entries.find(s => s.name === NexusSearchSchemaEntryNames.variety)?.id ?? null, [searchSchema.entries]);

    /**
     * Resets the removedFilterIds when the searchToken changes.
     */
    useEffect(() => {
        setRemovedFilters([]);
    }, [searchData?.filters, searchToken, searchData?.keyword, searchData?.variance]);

    /**@type {boolean}*/
    const canSubmit = useMemo(() => !!state.imageFile || !!state.message?.trim()?.length, [state.imageFile, state.message])

    /**
     * Sets the message of the state.
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    const onMessageChanged = useCallback((e) => {
        e.persist();
        setState(prevState => ({
            ...prevState,
            message: e.target?.value ?? '',
        }))
    }, [])

    /**
     * Submits a lost search report.
     * @return {Promise<void>}
     */
    const submit = useCallback(async () => {
        if (!state.imageFile && !state.message?.trim()?.length)
            return;
        setSubmitting(true);
        const response = await Api.submitLostNexusSearch({
            searchToken: searchToken,
            lostSearchDescription: state.message,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setSubmitted(true);
        }
        setSubmitting(false);
    }, [state?.imageFile, state?.message, searchToken, isMounted])

    /**
     * Applies the removed filters to the search data.
     * Clears variance when the variety filter chip is among the removals.
     * @type {(function(): void)}
     */
    const applyRemovedFilters = useCallback(() => {
        const varietyRemoved = !!varietyFilterId && removedFilters.some(e => e.filterId === varietyFilterId);
        setSearchData(prevState => ({
            ...prevState,
            keyword: removedFilters.some(e => e.filterId === null) ? null : prevState.keyword,
            filters: prevState.filters.filter(e => !removedFilters.find(r => r.filterId === e.filterId && r.value === e.value)),
            ...(varietyRemoved ? {variance: undefined} : {}),
        }))
    }, [setSearchData, removedFilters, varietyFilterId])

    /**
     * Removes all the filters from the search data
     * @type {(function(): void)}
     */
    const removeAllFilters = useCallback(() => {
        setSearchData(prevState => ({
            ...prevState,
            keyword: null,
            filters: [],
            variance: undefined,
        }))
    }, [setSearchData])

    const renderedFilters = useMemo(() => {
        return [
            ...(searchData?.keyword ? [{
                title: "Keyword",
                displayValue: searchData.keyword,
                filterId: null,
                value: searchData.keyword
            }] : []),
            ...(searchData?.variance ? [{
                key: 'variance-filter',
                title: "More options of",
                displayValue: searchData.variance.partNo ?? "Part Number",
                filterId: varietyFilterId,
                value: searchData.variance.variety,
            }] : []),
            ...searchData?.filters
                .map(e => {
                    const schemaEntry = searchSchema.entries.find(s => s.id === e.filterId);
                    if (!schemaEntry)
                        return null
                    // Remove the variance filter from the list of filters to be rendered
                    if (varietyFilterId && e.filterId === varietyFilterId)
                        return null;
                    return {
                        ...e,
                        title: schemaEntry?.title ?? "",
                        displayValue: Utils.formatTechnicalPropertyValueDisplay(schemaEntry?.dataType, e.value, schemaEntry?.nullReplacement),
                        ...(schemaEntry?.dataType === ProductTechnicalPropertyTypeIds.color ? {tooltipValue: e.value} : {}),
                    }
                })
                .filter(e => !!e)
            ?? [],
        ];
    }, [searchData?.filters, searchData?.keyword, searchData?.variance, searchSchema?.entries, varietyFilterId])

    return (
        <Card variant="elevation" sx={{boxShadow: 'none'}} className={'lost-search-container'}>
            {
                submitted &&
                <div className={'lost-search-inner-container'}>
                    <p className={'title'}>
                        Your note has been sent successfully
                    </p>
                    <LostSearchSubmitted className={'svg-art-like-constrained'}/>
                </div>
            }
            {
                !submitted &&
                <div className={'lost-search-inner-container'}>
                    <p className={'title text-center'}>
                        Sorry, we did not find any results for your search
                    </p>
                    {
                        renderedFilters?.length > 0 &&
                        <>
                            <div className={'d-flex justify-content-center align-items-center  mb-3'}>
                                <p className={'text-md font-weight-semi-bold'}>
                                    Your Searching Criteria:
                                </p>
                            </div>
                            <div className={'d-flex flex-wrap justify-content-center'} style={{gap: 4}}>
                                {
                                    renderedFilters.map((filter) => {
                                        const removed = !!removedFilters.find(e => e.filterId === filter.filterId && e.value === filter.value)
                                        return (
                                            <Chip
                                                key={filter.key ?? filter?.filterId}
                                                label={
                                                    <Box component={'span'} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                        <Typography variant={'body1'}>
                                                            {filter.title}:
                                                        </Typography>
                                                        <Typography variant={'body1'} fontWeight={500} color={'text.primary'}>
                                                            {filter.displayValue}
                                                        </Typography>
                                                    </Box>
                                                }
                                                disabled={readonly}
                                                onDelete={() => setRemovedFilters(prevState => {
                                                    const index = prevState.findIndex(e => e.filterId === filter.filterId && e.value === filter.value)
                                                    if (index === -1) {
                                                        return [...prevState, filter]
                                                    }
                                                    return prevState.filter(e => !(e.filterId === filter.filterId && e.value === filter.value))
                                                })}
                                                deleteIcon={
                                                    removed
                                                        ? <AddOutlined color={'success'}/>
                                                        : <CloseOutlined/>
                                                }
                                                className={classnames('chips text-lg', {'bg-transparent ': removed})}
                                                sx={{
                                                    ...(removed && {
                                                        border: '1px dashed transparent',
                                                        borderColor: 'text.disabled',
                                                        textDecoration: 'line-through',
                                                    })
                                                }}
                                                color={filter.filterId ? 'primary' : 'secondary'}
                                                variant={'outlined'}
                                                size={'small'}
                                            />
                                        )
                                    })
                                }
                            </div>
                            <div className={'mb-5 mt-3 d-flex justify-content-center, align-items-center'}>
                                {
                                    removedFilters.length > 0 &&
                                    <Button
                                        variant={'contained'}
                                        color={'primary'}
                                        className={'px-5 mr-2'}
                                        disabled={readonly}
                                        onClick={applyRemovedFilters}
                                    >
                                        Apply Changes
                                    </Button>
                                }
                                <Button
                                    color={'error'}
                                    variant={'contained'}
                                    className={'ml-2'}
                                    disabled={readonly}
                                    onClick={removeAllFilters}
                                >
                                    Remove all filters
                                </Button>
                            </div>
                        </>
                    }
                    {
                        showForm &&
                        <>
                            <p className={'description text-center'}>
                                Please send us a note about what you were looking for along with any relevant information regarding your
                                customer.
                            </p>
                            <TextField
                                fullWidth
                                variant={'outlined'}
                                type={'text'}
                                multiline
                                value={state.message}
                                onChange={onMessageChanged}
                                minRows={5}
                                disabled={readonly}
                                label={'Description'}
                            />
                            <Fade in={canSubmit}>
                                <Button
                                    disabled={submitting || !canSubmit || readonly}
                                    variant={'contained'}
                                    className={'mt-3 px-5'}
                                    color={'primary'}
                                    size={'medium'}
                                    onClick={submit}
                                >
                                    {
                                        submitting
                                            ? "Sending..."
                                            : "Report Lost Search"
                                    }
                                </Button>
                            </Fade>
                        </>
                    }
                </div>
            }
        </Card>
    )
}

export default LostSearchContainer;
